import { createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import type { Dispatch } from '@reduxjs/toolkit';

import { isForm, QUESTION_PERMISSIONS } from '@hubertai/screening-question-types';
import type {
  EQuestionType,
  TAnyClientQuestionType,
  TAvailableLanguageCodes,
  TLocalized,
  TQuestionDisplayMode,
} from '@hubertai/screening-question-types';
import { ECreateQuestionMode } from '../components/Questionsv2/builder/context/create-question.context';
import { initialQuestionState, setData } from './Question.Editor.Slice';
import type { QuestionEditorState } from './Question.Editor.Slice';
import {
  customParametersUpdate,
  setReplyOptionsInParameters,
  setCustomEvaluation,
  updateOptionEnabledInParameters,
} from './TemplateSlice';
import type { RootState } from 'app/store';
import type { TextData, TextDataWithValueIds, TIsDefaultValue } from 'types/questions/question.types.v4';
import type { TSchedulingConfig } from 'types/questions/scheduling.types';
import type { TQuestionRule } from 'types/rules.types';
import { isHubertAdminUser } from 'types/user.types';
import type { TAdminUser, TRecruiterAppUser } from 'types/user.types';

import { EEventType } from 'types/events.enum';
import type { TTemplateParameterKeys, TVisibility } from 'types/template/template.types';

interface InitializeEditorParams {
  mode: ECreateQuestionMode;
  nodeId?: string;
  generatedId: string;
  inTemplateEditAndUneditable: boolean;
  questionConfig: TAnyClientQuestionType | undefined;
  questionVersion: number;
  language?: TAvailableLanguageCodes;
  templateLanguage?: TAvailableLanguageCodes;
  user: TRecruiterAppUser | TAdminUser;
  clientId?: string;

  // Text data
  intros?: TextData;
  texts?: TextData;
  outros?: TextData;
  clarifies?: TextData;
  options?: any;
  // Actions
  pre_actions: { isDefault: boolean; value: string[] };
  post_actions: { isDefault: boolean; value: string[] };
  isPublishedOrNonExclusiveQuestion: boolean;
  hasQuestionUpdatePermission: boolean;
  scoring_category?: string;
  weight?: number;
  /**
   * The scope of the template the editor was opened from, when there is one. Only consumed for a
   * brand-new question — see getNewQuestionTemplateScope.
   */
  templateVisibilitySettings?: TTemplateVisibilitySettings;
}

export type TTemplateVisibilitySettings = {
  visibility: TVisibility | null;
  accessGroupIds?: string[];
};

// Both of these produce a NEW question that the template has to be able to deliver, so both take
// the template's scope. Every other mode keeps the scope of the question it is based on.
const NEW_QUESTION_IN_TEMPLATE_MODES: ECreateQuestionMode[] = [
  ECreateQuestionMode.InTemplateCreate,
  ECreateQuestionMode.InTemplateCopy,
];

/**
 * Scope overrides for a question being created or copied inside a template, which should land in the
 * same visibility and access groups as the template that will deliver it. A copy needs this as much
 * as a create does: keeping the source question's scope would leave a recruiter's copy of a global
 * question `global`, which the backend refuses outright for recruiters.
 *
 * Deliberately excludes the edit modes. Applying the template's scope to an edit is what blanked the
 * scope of every question edited outside a template (no template in the store means no scope), which
 * an update then persisted. Returns undefined to mean "keep the existing scope".
 */
export const getNewQuestionTemplateScope = (
  mode: ECreateQuestionMode,
  templateVisibilitySettings?: TTemplateVisibilitySettings,
): { visibility: TVisibility; access_groups_ids?: string[] } | undefined => {
  // Standalone Create/Copy have no template. They must not read one out of the store either: the
  // template slice can still hold whichever template was last visited.
  if (!NEW_QUESTION_IN_TEMPLATE_MODES.includes(mode)) return undefined;

  const visibility = templateVisibilitySettings?.visibility;
  // Template scope not resolved (yet) — fall back to the default rather than write a null scope.
  if (!visibility) return undefined;

  return { visibility, access_groups_ids: templateVisibilitySettings?.accessGroupIds };
};

const getActionsArray = (actions: TIsDefaultValue): string[] => {
  if (Array.isArray(actions)) return actions;
  if (actions && typeof actions === 'object' && actions.value) {
    return getActionsArray(actions.value);
  }
  return [];
};

export const initializeQuestionEditor = createAsyncThunk<
  { questionId: string; inTemplateEditAndUneditable: boolean } | undefined,
  InitializeEditorParams,
  { state: RootState }
>('questionEditor/initialize', async (params, { dispatch }) => {
  const {
    mode,
    nodeId,
    generatedId,
    questionConfig,
    questionVersion,
    language,
    templateLanguage,
    user,
    intros,
    texts,
    outros,
    clarifies,
    options,
    pre_actions,
    post_actions,
    isPublishedOrNonExclusiveQuestion,
    hasQuestionUpdatePermission,
    inTemplateEditAndUneditable,
    templateVisibilitySettings,
  } = params;

  if (questionVersion !== 4 && questionConfig) {
    console.log('Legacy question: nodeId', nodeId);
    return;
  }

  const isReadOnly = () => {
    if (isHubertAdminUser(user)) return false;
    const recruiterUser = user as TRecruiterAppUser;
    const hasPermissions =
      recruiterUser.access_groups_permissions?.some((ag) => ag.permissions.includes(QUESTION_PERMISSIONS.Create)) ||
      recruiterUser.access_groups_permissions?.some((ag) =>
        ag.permissions.includes(QUESTION_PERMISSIONS.ManageClientLevel),
      );
    return !hasPermissions;
  };

  const isCopyMode = [ECreateQuestionMode.InTemplateCopy, ECreateQuestionMode.Copy].includes(mode);
  const effectiveIsPublishedOrNonExclusiveQuestion = isCopyMode ? false : isPublishedOrNonExclusiveQuestion;
  const effectiveHasQuestionUpdatePermission = isCopyMode ? true : hasQuestionUpdatePermission;

  try {
    if (questionConfig) {
      const commonData = buildQuestionData(questionConfig, isCopyMode, {
        language,
        templateLanguage,
        intros,
        texts,
        outros,
        clarifies,
        options,
        pre_actions,
        post_actions,
        isPublishedOrNonExclusiveQuestion: effectiveIsPublishedOrNonExclusiveQuestion,
        hasQuestionUpdatePermission: effectiveHasQuestionUpdatePermission,
        mode,
        isReadonly: isReadOnly(),
        inTemplateEditAndUneditable,
        generatedId,
        clientId: params.clientId,
        templateVisibilitySettings,
      });
      dispatch(setData(commonData));
      return { questionId: commonData.id, inTemplateEditAndUneditable };
    } else {
      console.log('initializeQuestionEditor - newQuestion');
      const newQuestion = {
        ...initialQuestionState,
        ...getNewQuestionTemplateScope(mode, templateVisibilitySettings),
        mode,
        client_id: params.clientId,
        language: language ?? templateLanguage ?? 'en-gb',
        isPublishedOrNonExclusiveQuestion: effectiveIsPublishedOrNonExclusiveQuestion,
        hasQuestionUpdatePermission: effectiveHasQuestionUpdatePermission,
        inTemplateEditAndUneditable,
        id: generatedId,
        intros: intros ? convertTextData(intros) : undefined,
        texts: texts ? convertTextData(texts) : undefined,
        outros: outros ? convertTextData(outros) : undefined,
        clarifies: clarifies ? convertTextData(clarifies) : undefined,
      };
      dispatch(setData(newQuestion));
      return { questionId: newQuestion.id, inTemplateEditAndUneditable };
    }
  } catch (error) {
    console.error('Initialization failed:', error);
    return;
  }
});

export const updateInTemplateOptionEvaluation = (
  templateId: string | null,
  optionId: string,
  questionId: string,
  type: EQuestionType,
  dispatch: Dispatch,
  mode: ECreateQuestionMode,
  value: any,
  removeRuleIds?: string[],
  currentRules?: TQuestionRule[],
) => {
  if (templateId && mode === ECreateQuestionMode.InTemplateEdit) {
    // add to template evaluation object
    dispatch(
      setCustomEvaluation({
        templateId,
        eventType: EEventType.MODIFIED,
        resource: {
          nodeId: questionId,
          nodeType: type,
          evaluation: value,
          key: `${optionId}`,
          removeRuleIds,
          currentRules,
        },
        description: `User updated the evaluation of ${questionId}/${optionId} to ${JSON.stringify(value)}.`,
      }),
    );
  }
};

// MPG-248 add ability to sort options on a template level
export const sortOptions = (options: any[], templateOptions: any) => {
  if (!templateOptions || Object.keys(templateOptions).length === 0) return options;
  const order = Object.keys(templateOptions);

  // Check if all current options are in the parameters
  const isComplete = options.every((option) => order.includes(option.id));
  if (!isComplete) return options;

  return [...options].sort((a, b) => {
    const aIndex = order.indexOf(a.id);
    const bIndex = order.indexOf(b.id);
    return aIndex - bIndex;
  });
};

export const buildTemplateOptions = (options: any[], currentTemplateOptions: any) => {
  const templateOptions: Record<string, any> = {};
  options.forEach((option) => {
    templateOptions[option.id] = currentTemplateOptions?.[option.id] || {};
  });
  return templateOptions;
};

export const updateInTemplateOptionOrder = (
  templateId: string | null,
  questionId: string,
  options: any[],
  currentTemplateOptions: any,
  dispatch: Dispatch,
) => {
  if (templateId) {
    // Construct the options object for parameters where key order is order
    const templateOptions = buildTemplateOptions(options, currentTemplateOptions);

    dispatch(
      customParametersUpdate({
        templateId,
        eventType: EEventType.MODIFIED,
        resource: {
          nodeId: questionId,
          key: 'options' as any,
          language: undefined as any,
          value: templateOptions,
          isDefault: false,
        },
        description: `User updated the order of options for ${questionId}.`,
      }),
    );
  }
};

export const updateInTemplateOptionEnabled = (
  templateId: string | null,
  optionId: string,
  questionId: string,
  language: TAvailableLanguageCodes,
  dispatch: Dispatch,
  mode: ECreateQuestionMode,
  value: boolean,
) => {
  if (templateId && mode === ECreateQuestionMode.InTemplateEdit) {
    dispatch(
      updateOptionEnabledInParameters({
        templateId,
        eventType: EEventType.MODIFIED,
        resource: {
          nodeId: questionId,
          optionId,
          language,
          value,
        },
        description: `User updated the enabled/visible state of ${questionId}/${optionId} to ${value}.`,
      }),
    );
  }
};

export const updateInTemplateReplyOptions = ({
  templateId,
  questionId,
  optionId,
  replies,
  operation,
  itemIndex,
  newReply,
  language,
  dispatch,
  mode,
}: {
  templateId: string | null;
  questionId: string;
  optionId?: string;
  replies: string[];
  operation: 'edit' | 'add' | 'remove';
  itemIndex?: number;
  newReply?: string;
  language: TAvailableLanguageCodes;
  dispatch: Dispatch;
  mode: ECreateQuestionMode;
}) => {
  if (templateId && optionId && mode === ECreateQuestionMode.InTemplateEdit) {
    const nextReplies = [...replies];

    if (operation === 'add') {
      nextReplies.push('utter_confirm');
    }

    if (operation === 'edit' && typeof itemIndex === 'number' && typeof newReply === 'string') {
      nextReplies[itemIndex] = newReply;
    }

    if (operation === 'remove' && typeof itemIndex === 'number') {
      nextReplies.splice(itemIndex, 1);
    }

    dispatch(
      setReplyOptionsInParameters({
        templateId,
        eventType: EEventType.MODIFIED,
        resource: {
          nodeId: questionId,
          optionId,
          replies: nextReplies,
          language,
        },
        description: `User ${operation}d reply options for ${questionId}/${optionId}/${language}.`,
      }),
    );
  }
};

export const updateCustomParametersInTemplate = (
  templateId: string | null,
  questionId: string,
  dispatch: Dispatch,
  mode: ECreateQuestionMode,
  value: number | undefined,
  key: TTemplateParameterKeys,
  description: string,
) => {
  if (templateId && mode === ECreateQuestionMode.InTemplateEdit) {
    dispatch(
      customParametersUpdate({
        templateId,
        eventType: EEventType.MODIFIED,
        resource: {
          nodeId: questionId,
          key,
          value,
          isDefault: false,
        },
        description,
      }),
    );
  }
};

export const remapRuleIdsForCopy = (rules: TQuestionRule[], idMap: Record<string, string>): TQuestionRule[] => {
  const remapValue = (value: string | number): string | number =>
    typeof value === 'string' && value in idMap ? idMap[value] : value;

  return rules.map((rule) => ({
    ...rule,
    conditions: rule.conditions.map((condition) => {
      const isSelfReferencing = !condition.question_id || condition.question_id in idMap;
      if (!isSelfReferencing) {
        return condition;
      }

      const value = Array.isArray(condition.value)
        ? (condition.value as (string | number)[]).map(remapValue)
        : remapValue(condition.value);

      return {
        ...condition,
        ...(condition.question_id ? { question_id: idMap[condition.question_id] } : {}),
        value: value as typeof condition.value,
      };
    }),
    actions: rule.actions?.map((action) => ({
      ...action,
      value: action.value.map((v) => {
        if (typeof v === 'string') {
          return v in idMap ? idMap[v] : v;
        }
        return v.id in idMap ? { ...v, id: idMap[v.id] } : v;
      }),
    })),
  }));
};

const buildQuestionData = (
  questionConfig: TAnyClientQuestionType,
  isCopy: boolean,
  {
    language,
    templateLanguage,
    intros,
    texts,
    outros,
    clarifies,
    options,
    pre_actions,
    post_actions,
    isPublishedOrNonExclusiveQuestion,
    hasQuestionUpdatePermission,
    mode,
    isReadonly,
    inTemplateEditAndUneditable,
    generatedId,
    clientId,
    templateVisibilitySettings,
  }: {
    language?: TAvailableLanguageCodes;
    templateLanguage?: TAvailableLanguageCodes;
    intros?: TextData;
    texts?: TextData;
    outros?: TextData;
    clarifies?: TextData;
    options?: any;
    // Actions
    pre_actions: { isDefault: boolean; value: string[] };
    post_actions: { isDefault: boolean; value: string[] };
    isPublishedOrNonExclusiveQuestion: boolean;
    hasQuestionUpdatePermission: boolean;
    mode: ECreateQuestionMode;
    isReadonly: boolean;
    inTemplateEditAndUneditable: boolean;
    generatedId: string;
    clientId?: string;
    templateVisibilitySettings?: TTemplateVisibilitySettings;
  },
): QuestionEditorState => {
  const templateScope = getNewQuestionTemplateScope(mode, templateVisibilitySettings);
  const copiedIdMap: Record<string, string> = isCopy
    ? {
        [questionConfig.id]: generatedId,
      }
    : {};

  //For copying a question we need a new id
  const withNewIds = (items: any[]) =>
    isCopy
      ? items.map((item) => {
          const newId = nanoid();
          copiedIdMap[item.id] = newId;
          return { ...item, id: newId };
        })
      : items;

  const language2 = language ?? templateLanguage ?? 'en-gb';
  return {
    forms: 'forms' in questionConfig && isForm(questionConfig) ? withNewIds(questionConfig.forms) : [],
    intents: 'intents' in questionConfig ? withNewIds(questionConfig.intents) : [],
    options: 'options' in questionConfig ? sortOptions(withNewIds(questionConfig.options), options) : [],
    rules: (() => {
      if (!('rules' in questionConfig)) return [];
      const rules = questionConfig.rules as TQuestionRule[] | undefined;
      if (!rules) return rules;
      return isCopy ? remapRuleIdsForCopy(rules, copiedIdMap) : rules;
    })(),
    id: isCopy ? generatedId : questionConfig.id,

    // A copy into a template adopts the template's scope; an edit keeps the question's own.
    ...(templateScope
      ? { visibility: templateScope.visibility, access_groups_ids: templateScope.access_groups_ids }
      : { visibility: questionConfig.visibility, access_groups_ids: questionConfig.access_groups_ids }),
    // The template's client always wins for a copy — a question can only live in one client, and the
    // template's is the one that has to render it.
    client_id: templateScope ? (clientId ?? questionConfig.client_id) : questionConfig.client_id,

    isReadOnly: isReadonly,
    isPublishedOrNonExclusiveQuestion,
    hasQuestionUpdatePermission,
    scoring_category: 'scoring_category' in questionConfig ? questionConfig.scoring_category : '',
    weight: 'weight' in questionConfig ? questionConfig.weight : 0,
    pre_actions: getActionsArray(pre_actions),
    post_actions: getActionsArray(post_actions),

    _id: isCopy ? undefined : questionConfig._id,
    source_question_id: isCopy ? questionConfig?._id : undefined,
    version: questionConfig.version,
    type: questionConfig.type,
    short_description: questionConfig.short_description,
    language: language2,
    availableLanguages: questionConfig.available_languages,
    presentation: questionConfig.presentation,
    min_selections: 'min_selections' in questionConfig ? questionConfig.min_selections : undefined,
    max_selections: 'max_selections' in questionConfig ? questionConfig.max_selections : undefined,
    min_required: 'min_required' in questionConfig ? questionConfig.min_required : undefined,
    help_text: 'help_text' in questionConfig ? questionConfig.help_text : undefined,
    ranking_mode:
      'ranking_mode' in questionConfig ? (questionConfig.ranking_mode as 'click' | 'dnd' | undefined) : undefined,
    display_mode:
      'display_mode' in questionConfig ? (questionConfig.display_mode as TQuestionDisplayMode | undefined) : undefined,
    intro: intros
      ? {
          isDefault: intros[language2]?.isDefault ?? true,
          value: convertTexts(intros[language2]?.value ?? questionConfig?.intro?.[language2] ?? []),
        }
      : { isDefault: true, value: [] },
    text: texts
      ? {
          isDefault: texts[language2]?.isDefault ?? true,
          value: convertTexts(texts[language2]?.value ?? questionConfig?.text?.[language2] ?? []),
        }
      : { isDefault: true, value: [] },
    outro: outros
      ? {
          isDefault: outros[language2]?.isDefault ?? true,
          value: convertTexts(outros[language2]?.value ?? questionConfig?.outro?.[language2] ?? []),
        }
      : { isDefault: true, value: [] },
    clarify: clarifies
      ? {
          isDefault: clarifies[language2]?.isDefault ?? true,
          value: convertTexts(
            clarifies[language2]?.value ??
              (questionConfig as { clarify?: TLocalized<string[]> })?.clarify?.[language2] ??
              [],
          ),
        }
      : { isDefault: true, value: [] },

    conversation_settings: 'conversation_settings' in questionConfig ? questionConfig.conversation_settings : undefined,
    send_data_to_looker:
      'send_data_to_looker' in questionConfig ? (questionConfig.send_data_to_looker as boolean) : false,
    deferrable: questionConfig.deferrable,
    classification_names: questionConfig.classification_names,
    plain_text: questionConfig.plain_text,
    stateModified: false,
    mode,
    previewRefreshKey: 0,
    inTemplateEditAndUneditable,
    intros: intros ? convertTextData(intros) : undefined,
    texts: texts ? convertTextData(texts) : undefined,
    outros: outros ? convertTextData(outros) : undefined,
    clarifies: clarifies ? convertTextData(clarifies) : undefined,
    questionConfig,
    copiedIdMap: isCopy ? copiedIdMap : undefined,
    allow_retake: 'allow_retake' in questionConfig ? questionConfig.allow_retake : undefined,
    required: 'required' in questionConfig ? (questionConfig as { required?: boolean }).required : undefined,
    max_retakes: 'max_retakes' in questionConfig ? questionConfig.max_retakes : undefined,
    max_thinking_seconds: 'max_thinking_seconds' in questionConfig ? questionConfig.max_thinking_seconds : undefined,
    min_duration_seconds: 'min_duration_seconds' in questionConfig ? questionConfig.min_duration_seconds : undefined,
    max_duration_seconds: 'max_duration_seconds' in questionConfig ? questionConfig.max_duration_seconds : undefined,
    recording_quality: 'quality' in questionConfig ? questionConfig.quality : undefined,
    slot_filling: 'slot_filling' in questionConfig ? questionConfig.slot_filling : undefined,
    objectives: 'objectives' in questionConfig ? questionConfig.objectives : undefined,
    scheduling:
      'scheduling' in questionConfig ? (questionConfig as { scheduling?: TSchedulingConfig }).scheduling : undefined,
    replies:
      'replies' in questionConfig
        ? (questionConfig as { replies?: Partial<Record<string, string[]>> }).replies
        : undefined,
  };
};

export const convertTexts = (texts: string[]) => texts.map((x) => ({ id: nanoid(), text: x }));
export const convertTextData = (data: TextData): TextDataWithValueIds =>
  Object.fromEntries(
    Object.entries(data).map(([lang, payload]) => [lang, { ...payload, value: convertTexts(payload.value) }]),
  );
